import { useEffect, useMemo, useState } from "react";
import api, { brl } from "../lib/api";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  AlertTriangle,
  Upload,
  Percent,
  CheckSquare,
  Square,
  Trash,
  Check,
  Camera,
  ImagePlus,
  Sparkles,
} from "lucide-react";
import ProductScanner from "../components/ProductScanner";

const EMPTY = {
  name: "",
  sku: "",
  category: "",
  cost_price: 0,
  sale_price: 0,
  stock: 0,
  min_stock: 5,
  description: "",
  image_url: "",
  is_public: true,
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState(
    "name,sku,category,cost_price,sale_price,stock,min_stock\n"
  );
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    price_percent: "",
    cost_percent: "",
    category: "",
    is_public: "",
    min_stock: "",
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [inlineEdit, setInlineEdit] = useState(null); // { id, field }
  const [inlineValue, setInlineValue] = useState("");

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        (p.sku || "").toLowerCase().includes(s) ||
        (p.category || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const save = async () => {
    try {
      if (editId) {
        await api.patch(`/products/${editId}`, form);
        toast.success("Produto atualizado");
      } else {
        const { data } = await api.post("/products", form);
        if (data._merged) {
          toast.success(
            `Já existia! +${data._added_stock} un. no estoque (total ${data.stock})`
          );
        } else {
          toast.success("Produto criado");
        }
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Excluído");
    load();
  };

  const edit = (p) => {
    setForm({ ...EMPTY, ...p });
    setEditId(p.id);
    setOpen(true);
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };

  const doBulkUpdate = async () => {
    const payload = { ids: [...selected] };
    Object.entries(bulkForm).forEach(([k, v]) => {
      if (v === "") return;
      if (k === "is_public") payload[k] = v === "true";
      else if (k === "price_percent" || k === "cost_percent" || k === "min_stock")
        payload[k] = Number(v);
      else payload[k] = v;
    });
    if (Object.keys(payload).length <= 1) {
      toast.error("Preencha pelo menos um campo");
      return;
    }
    try {
      const { data } = await api.post("/products/bulk-update", payload);
      toast.success(`${data.updated} produtos atualizados`);
      setBulkOpen(false);
      setBulkForm({
        price_percent: "",
        cost_percent: "",
        category: "",
        is_public: "",
        min_stock: "",
      });
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const doBulkDelete = async () => {
    if (!window.confirm(`Excluir ${selected.size} produtos selecionados?`))
      return;
    try {
      const { data } = await api.post("/products/bulk-delete", {
        ids: [...selected],
      });
      toast.success(`${data.deleted} excluídos`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const doDeleteAll = async () => {
    const msg =
      "⚠️  Isso vai APAGAR TODOS os produtos do estoque.\n\n" +
      'Digite "APAGAR TUDO" para confirmar:';
    const confirm = window.prompt(msg, "");
    if (confirm !== "APAGAR TUDO") {
      toast.info("Cancelado");
      return;
    }
    try {
      const { data } = await api.post("/products/delete-all", {
        confirm: "APAGAR TUDO",
      });
      toast.success(`${data.deleted} produtos apagados`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((ln) => {
      const cols = ln.split(",");
      const row = {};
      header.forEach((h, i) => {
        const v = (cols[i] || "").trim();
        if (["cost_price", "sale_price"].includes(h))
          row[h] = parseFloat(v) || 0;
        else if (["stock", "min_stock"].includes(h))
          row[h] = parseInt(v, 10) || 0;
        else row[h] = v;
      });
      return row;
    });
  };

  const doImportCsv = async () => {
    const rows = parseCsv(csvText).filter((r) => r.name);
    if (rows.length === 0) {
      toast.error("Nenhuma linha válida no CSV");
      return;
    }
    try {
      const { data } = await api.post("/products/import-csv", {
        rows,
        merge_duplicates: true,
      });
      toast.success(
        `${data.created} criados + ${data.merged} somados ao estoque`
      );
      setCsvOpen(false);
      setCsvText("name,sku,category,cost_price,sale_price,stock,min_stock\n");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no CSV");
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(r.result);
    r.readAsText(f);
  };

  const startInline = (id, field, current) => {
    setInlineEdit({ id, field });
    setInlineValue(String(current ?? ""));
  };
  const commitInline = async () => {
    if (!inlineEdit) return;
    const prod = items.find((p) => p.id === inlineEdit.id);
    if (!prod) return;
    const val =
      inlineEdit.field === "stock" ||
      inlineEdit.field === "min_stock"
        ? parseInt(inlineValue, 10) || 0
        : inlineEdit.field === "cost_price" || inlineEdit.field === "sale_price"
        ? parseFloat(inlineValue) || 0
        : inlineValue;
    try {
      await api.patch(`/products/${prod.id}`, {
        ...prod,
        [inlineEdit.field]: val,
      });
      toast.success("Atualizado");
      setInlineEdit(null);
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const InlineCell = ({ p, field, render }) => {
    const active =
      inlineEdit && inlineEdit.id === p.id && inlineEdit.field === field;
    if (active) {
      return (
        <div className="flex items-center gap-1">
          <input
            autoFocus
            type={
              ["stock", "min_stock", "cost_price", "sale_price"].includes(
                field
              )
                ? "number"
                : "text"
            }
            value={inlineValue}
            onChange={(e) => setInlineValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitInline();
              if (e.key === "Escape") setInlineEdit(null);
            }}
            className="w-24 px-1 py-0.5 border border-[#2563EB] rounded-sm text-sm"
          />
          <button
            onClick={commitInline}
            className="text-emerald-600 hover:bg-emerald-50 p-0.5 rounded-sm"
          >
            <Check size={12} />
          </button>
        </div>
      );
    }
    return (
      <button
        onClick={() => startInline(p.id, field, p[field])}
        className="hover:bg-blue-50 rounded-sm px-1 text-right w-full"
        title="Clique para editar"
      >
        {render(p[field])}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <header className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
            Controle de Estoque
          </div>
          <h1 className="font-display text-4xl font-bold">Produtos</h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} produtos · Ao cadastrar um produto já existente
            (mesmo nome/SKU), o sistema soma a quantidade ao estoque.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setScannerOpen(true)}
            data-testid="product-scan-btn"
            className="bg-gradient-to-r from-[#2563EB] to-[#0A1628] hover:opacity-90 text-white px-3 py-2 rounded-sm text-sm flex items-center gap-1.5 font-semibold"
          >
            <Camera size={14} /> Cadastro por foto (IA)
          </button>
          <button
            onClick={() => setCsvOpen(true)}
            data-testid="product-import-csv"
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-sm text-sm flex items-center gap-1.5"
          >
            <Upload size={14} /> Importar CSV
          </button>
          <button
            onClick={doDeleteAll}
            data-testid="product-delete-all"
            className="border border-red-300 hover:bg-red-50 text-red-600 px-3 py-2 rounded-sm text-sm flex items-center gap-1.5"
          >
            <Trash size={14} /> Apagar estoque
          </button>
          <button
            onClick={() => {
              setForm(EMPTY);
              setEditId(null);
              setOpen(true);
            }}
            data-testid="product-new-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"
          >
            <Plus size={16} /> Novo Produto
          </button>
        </div>
      </header>

      <div className="flex gap-3 items-center flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome, SKU ou categoria…"
          data-testid="products-search"
          className="flex-1 min-w-[240px] px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
        />
        {selected.size > 0 && (
          <div
            className="flex items-center gap-2 bg-[#0A1628] text-white px-3 py-1.5 rounded-sm"
            data-testid="bulk-action-bar"
          >
            <span className="text-xs font-semibold">
              {selected.size} selecionado(s)
            </span>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-edit-btn"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] px-3 py-1 rounded-sm text-xs font-semibold flex items-center gap-1"
            >
              <Percent size={12} /> Editar em massa
            </button>
            <button
              onClick={doBulkDelete}
              data-testid="bulk-delete-btn"
              className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded-sm text-xs font-semibold flex items-center gap-1"
            >
              <Trash2 size={12} /> Excluir
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs underline"
            >
              limpar
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-3 w-8">
                <button onClick={toggleAll} data-testid="toggle-all">
                  {selected.size === filtered.length && filtered.length > 0 ? (
                    <CheckSquare size={14} className="text-[#2563EB]" />
                  ) : (
                    <Square size={14} />
                  )}
                </button>
              </th>
              <th className="text-left p-3">Produto</th>
              <th className="text-left p-3">SKU</th>
              <th className="text-left p-3">Categoria</th>
              <th className="text-right p-3">Custo</th>
              <th className="text-right p-3">Venda</th>
              <th className="text-right p-3">Estoque</th>
              <th className="text-center p-3">Catálogo</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${
                  selected.has(p.id) ? "bg-blue-50" : ""
                }`}
                data-testid={`product-row-${p.id}`}
              >
                <td className="p-3">
                  <button onClick={() => toggleOne(p.id)}>
                    {selected.has(p.id) ? (
                      <CheckSquare size={14} className="text-[#2563EB]" />
                    ) : (
                      <Square size={14} />
                    )}
                  </button>
                </td>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3 text-slate-500 text-xs">{p.sku || "—"}</td>
                <td className="p-3 text-slate-500 text-xs">
                  {p.category || "—"}
                </td>
                <td className="p-3 text-right text-slate-600">
                  <InlineCell
                    p={p}
                    field="cost_price"
                    render={(v) => brl(v)}
                  />
                </td>
                <td className="p-3 text-right font-semibold text-[#2563EB]">
                  <InlineCell
                    p={p}
                    field="sale_price"
                    render={(v) => brl(v)}
                  />
                </td>
                <td className="p-3 text-right">
                  <InlineCell
                    p={p}
                    field="stock"
                    render={(v) => (
                      <span
                        className={
                          v <= 0
                            ? "text-red-600 font-bold"
                            : v <= p.min_stock
                              ? "text-amber-600 font-semibold"
                              : ""
                        }
                      >
                        {v <= 0 ? (
                          <>
                            <AlertTriangle size={12} className="inline mr-1" />
                            ESGOTADO
                          </>
                        ) : (
                          <>
                            {v <= p.min_stock && (
                              <AlertTriangle size={12} className="inline mr-1" />
                            )}
                            {v}
                          </>
                        )}
                      </span>
                    )}
                  />
                </td>
                <td className="p-3 text-center">
                  {p.is_public ? (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm">
                      ATIVO
                    </span>
                  ) : (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm">
                      OFF
                    </span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => edit(p)}
                    data-testid={`product-edit-${p.id}`}
                    className="p-1 hover:bg-slate-200 rounded-sm mr-1"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`product-delete-${p.id}`}
                    className="p-1 hover:bg-red-100 text-red-500 rounded-sm"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan="9"
                  className="p-12 text-center text-slate-500"
                >
                  {items.length === 0
                    ? 'Estoque vazio. Clique em "Novo Produto" ou "Importar CSV" para começar.'
                    : "Nenhum produto encontrado com esse filtro."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New/Edit Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-sm max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            data-testid="product-modal"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl font-bold">
                {editId ? "Editar" : "Novo"} Produto
              </h2>
              <button
                onClick={() => setOpen(false)}
                data-testid="product-modal-close"
              >
                <X size={20} />
              </button>
            </div>
            {!editId && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs p-2.5 rounded-sm mb-3">
                💡 Se já existir um produto com o mesmo nome ou SKU, o estoque
                será somado e o custo atualizado pela média ponderada.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {[
                ["name", "Nome *", "text", "col-span-2"],
                ["sku", "SKU", "text", ""],
                ["category", "Categoria", "text", ""],
                ["cost_price", "Preço de Custo", "number", ""],
                ["sale_price", "Preço de Venda *", "number", ""],
                ["stock", "Quantidade a adicionar", "number", ""],
                ["min_stock", "Estoque Mínimo", "number", ""],
                ["description", "Descrição", "textarea", "col-span-2"],
              ].map(([k, label, type, extra]) => (
                <div key={k} className={extra}>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    {label}
                  </label>
                  {type === "textarea" ? (
                    <textarea
                      rows={3}
                      value={form[k] || ""}
                      onChange={(e) =>
                        setForm({ ...form, [k]: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                      data-testid={`product-${k}-input`}
                    />
                  ) : (
                    <input
                      type={type}
                      value={form[k] || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [k]:
                            type === "number"
                              ? Number(e.target.value)
                              : e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                      data-testid={`product-${k}-input`}
                    />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 col-span-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) =>
                    setForm({ ...form, is_public: e.target.checked })
                  }
                  data-testid="product-is-public"
                />
                Exibir no catálogo público
              </label>
              <div className="col-span-2 border border-slate-200 rounded-sm p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wider text-slate-600 mb-2 font-semibold">
                  Foto do produto
                </div>
                {form.image_url ? (
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={
                        form.image_url.startsWith("/api/")
                          ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`
                          : form.image_url
                      }
                      alt=""
                      className="w-20 h-20 rounded-sm object-cover border border-slate-200"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-slate-500 truncate">
                        {form.image_url}
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        className="text-xs text-red-500 hover:underline mt-1"
                      >
                        Remover foto
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <label
                    className="cursor-pointer bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5 font-semibold"
                    data-testid="product-upload-photo"
                  >
                    <ImagePlus size={12} /> Enviar foto
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const r = new FileReader();
                        r.onload = async () => {
                          const s = String(r.result);
                          const b64 = s.slice(s.indexOf(",") + 1);
                          try {
                            const { data } = await api.post(
                              "/products/upload-image",
                              {
                                image_base64: b64,
                                filename_hint: form.name,
                              }
                            );
                            setForm({ ...form, image_url: data.image_url });
                            toast.success("Foto anexada ✓");
                          } catch {
                            toast.error("Erro ao enviar foto");
                          }
                        };
                        r.readAsDataURL(f);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid="product-gen-ai-photo"
                    disabled={!form.name}
                    onClick={async () => {
                      if (!form.name) return;
                      toast.info("Gerando foto com IA… pode demorar uns 10s");
                      try {
                        // Se produto já existe, usa a tool direta. Caso contrário,
                        // criaremos provisório — melhor só funcionar em edit.
                        if (!editId) {
                          toast.info(
                            "Salve o produto primeiro, depois peça à IA"
                          );
                          return;
                        }
                        await api.post("/ai/chat", {
                          message: `Gera imagem profissional pro produto "${form.name}" e coloca nele`,
                        });
                        const { data: p } = await api.get(
                          `/products`
                        );
                        const updated = p.find((x) => x.id === editId);
                        if (updated?.image_url) {
                          setForm({ ...form, image_url: updated.image_url });
                          toast.success("Foto gerada ✓");
                        }
                      } catch {
                        toast.error("Erro ao gerar");
                      }
                    }}
                    className="bg-gradient-to-r from-[#2563EB] to-[#0A1628] hover:opacity-90 disabled:opacity-50 text-white px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5 font-semibold"
                  >
                    <Sparkles size={12} /> Gerar com IA
                  </button>
                  <input
                    type="text"
                    value={form.image_url || ""}
                    onChange={(e) =>
                      setForm({ ...form, image_url: e.target.value })
                    }
                    placeholder="ou cole uma URL"
                    className="flex-1 min-w-[160px] px-2 py-1.5 border border-slate-300 rounded-sm text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                data-testid="product-save-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {csvOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-sm max-w-2xl w-full p-6"
            data-testid="csv-modal"
          >
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-display text-xl font-bold">
                Importar Produtos (CSV)
              </h2>
              <button onClick={() => setCsvOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-3">
              Formato:{" "}
              <code className="bg-slate-100 px-1 rounded-sm text-xs">
                name,sku,category,cost_price,sale_price,stock,min_stock
              </code>
              . Produtos já existentes terão o estoque somado.
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              data-testid="csv-file-input"
              className="mb-3 text-sm"
            />
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={10}
              data-testid="csv-textarea"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-xs font-mono"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setCsvOpen(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Cancelar
              </button>
              <button
                onClick={doImportCsv}
                data-testid="csv-import-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm text-sm font-semibold"
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-sm max-w-md w-full p-6"
            data-testid="bulk-modal"
          >
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-display text-xl font-bold">
                Editar {selected.size} produtos
              </h2>
              <button onClick={() => setBulkOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Deixe em branco o que não quiser alterar. Percentuais aceitam
              negativos (ex: -10 para desconto).
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Ajustar preço de venda (%)
                </label>
                <input
                  type="number"
                  value={bulkForm.price_percent}
                  onChange={(e) =>
                    setBulkForm({ ...bulkForm, price_percent: e.target.value })
                  }
                  data-testid="bulk-price-percent"
                  placeholder="ex: 10 ou -5"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Ajustar preço de custo (%)
                </label>
                <input
                  type="number"
                  value={bulkForm.cost_percent}
                  onChange={(e) =>
                    setBulkForm({ ...bulkForm, cost_percent: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Mudar categoria
                </label>
                <input
                  type="text"
                  value={bulkForm.category}
                  onChange={(e) =>
                    setBulkForm({ ...bulkForm, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Exibir no catálogo público?
                </label>
                <select
                  value={bulkForm.is_public}
                  onChange={(e) =>
                    setBulkForm({ ...bulkForm, is_public: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                >
                  <option value="">— não alterar —</option>
                  <option value="true">Ativo</option>
                  <option value="false">Oculto</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                  Estoque mínimo
                </label>
                <input
                  type="number"
                  value={bulkForm.min_stock}
                  onChange={(e) =>
                    setBulkForm({ ...bulkForm, min_stock: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setBulkOpen(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm"
              >
                Cancelar
              </button>
              <button
                onClick={doBulkUpdate}
                data-testid="bulk-apply-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm text-sm font-semibold"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Scanner Modal */}
      <ProductScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onCreated={() => load()}
      />
    </div>
  );
}
